import delay from '@redux-saga/delay-p';
import get from 'lodash/get';
import { all, call, fork, put, race, select, take } from 'typed-redux-saga/macro';

import { now } from 'Lib/utils/perfTimer';
import revokeObjectURL from 'Lib/utils/revokeObjectURL';
import { getServiceFn } from 'Services';
import { MEDIA_SERVICE } from 'Services/constants';

import { getIsConnected } from 'Store/websocket/selectors';

import { assignPlaceholders, placeholderConsumed } from './actions';
import { MEDIA_HYDRATION_NUDGE_RECEIVED, MEDIA_TYPE_PHOTO } from './constants';
import type { HandlersType, MediaInfoType } from './types';

const HYDRATION_WAIT_TIMEOUT = 20000;
const REVOKE_IMAGE_DELAY = 1000;
const DEFAULT_NUDGE_DELAY = 5000;

const MEDIA_TYPES_SERVICE_MAP = {
  [MEDIA_TYPE_PHOTO]: [MEDIA_SERVICE, 'uploadPhoto'],
};

function* deleteMedia(id: string) {
  const response = yield* call(yield* call(getServiceFn, MEDIA_SERVICE, 'deleteMedia' as const), [
    id,
  ]);
  return get(response, ['deleteMediaResponseModel']);
}

function* deletePlaceholder(id: string) {
  yield* call(deleteMedia, id);
  yield* put(placeholderConsumed(id));
}

function* requestPlaceholders(count = 1) {
  const response = yield* call(
    yield* call(getServiceFn, MEDIA_SERVICE, 'requestPlaceholders' as const),
    count
  );
  return response;
}

function* deletePlaceholders(ids: string[] = []) {
  yield* all(ids.map((id) => call(deletePlaceholder, id)));
}

function* reorderMedia(ids: string[]) {
  const response = yield* call(
    yield* call(getServiceFn, MEDIA_SERVICE, 'reorderMedia' as const),
    ids
  );
  return response;
}

function* updateMediaDetails(id: string, cropInfo: any, prompt: any) {
  try {
    const response = yield* call(
      yield* call(getServiceFn, MEDIA_SERVICE, 'updateMediaDetails' as const),
      id,
      cropInfo,
      prompt
    );
    return response;
  } catch (e) {
    // swallow the error
    return null;
  }
}

const matchedNudge = (placeholderId: string) => (action) => {
  const { type, payload } = action;
  if (type !== MEDIA_HYDRATION_NUDGE_RECEIVED) {
    return false;
  }
  const { mediaId } = payload || {};
  return mediaId === placeholderId;
};

function* uploadMediaItem({
  mediaType,
  blob,
  placeholderId,
  handlers: { onUploadSuccess, onUploadFailure },
  imageRef,
}: {
  mediaType: string;
  blob: MediaInfoType;
  placeholderId: string;
  handlers: HandlersType;
  imageRef: string;
}) {
  let result, startTime;

  const mediaReferences = {
    placeholderId,
    imageRef,
  };

  try {
    const methods = MEDIA_TYPES_SERVICE_MAP[mediaType];

    if (methods) {
      result = yield* call(yield* call(getServiceFn, methods[0], methods[1]), blob, placeholderId);

      startTime = now();
    }
  } catch (e) {
    if (onUploadFailure) {
      yield* call(onUploadFailure, e, mediaReferences);
    }

    return null;
  }

  const isWebsocketConnected = yield* select(getIsConnected);

  // This handles the development use case where websockets doesn't work
  // Also a fallback if websockets are disconnected for some reason...
  const allowNudgeToPass = !isWebsocketConnected;

  const { nudge, timeout } = yield* race({
    nudge: allowNudgeToPass ? delay(DEFAULT_NUDGE_DELAY) : take(matchedNudge(placeholderId)),
    timeout: delay(HYDRATION_WAIT_TIMEOUT),
  });

  if (timeout) {
    // delete placeholder id
    yield* call(deleteMedia, placeholderId);

    if (onUploadFailure) {
      yield* call(onUploadFailure, new Error('Upload timeout'), mediaReferences);
    }
  }

  if (nudge && onUploadSuccess) {
    if (onUploadSuccess) {
      yield* call(onUploadSuccess, nudge, mediaReferences, now() - startTime);
    }
  }

  return result;
}

function* uploadMedia(media: MediaInfoType[], { onUploadSuccess, onUploadFailure }: HandlersType) {
  const placeholders = yield* call(requestPlaceholders, media.length);
  const placeholderIds = placeholders.createPlaceholderResponseModel.mediaIds;
  yield* put(assignPlaceholders(media, placeholderIds));

  function* uploadItem(mediaType, blob, attributes, placeholderId) {
    // Create a short-lived image reference to be used in notifications
    // TODO: Should be a better way to do this, maybe something along the lines
    // of creating a universal image cache ...
    const image = window.URL.createObjectURL(blob);

    // delay revoking the image, as we might need it for notifications
    function* revokeImage() {
      yield delay(REVOKE_IMAGE_DELAY);
      revokeObjectURL(image);
    }

    try {
      yield* call(uploadMediaItem, {
        mediaType,
        blob,
        placeholderId,
        handlers: { onUploadSuccess, onUploadFailure },
        imageRef: image,
      });
    } catch (e: any) {
      yield* call(deletePlaceholders, [placeholderId]);
      yield* fork(revokeImage);
      throw new Error(e);
    }
    yield* put(placeholderConsumed(placeholderId));

    yield* fork(revokeImage);
  }

  yield* all(
    media.map(({ blob, mediaType = MEDIA_TYPE_PHOTO, attributes = {} }: MediaInfoType, index) =>
      call(uploadItem, mediaType, blob, attributes, placeholderIds[index])
    )
  );
}

// Public Sagas
export const uploadMediaSaga = uploadMedia;
export const deletePlaceholderSaga = deletePlaceholder;
export const deleteMediaSaga = deleteMedia;
export const reorderMediaSaga = reorderMedia;
export const updateMediaDetailsSaga = updateMediaDetails;
